/**
 * i18n bootstrap — DealAlert.
 *
 * Sprint I Phase 1: DE/EN/HR with `common`, `landing`, `auth`, `dashboard`.
 * Sprint M Phase 2: + `topdeals`, `favorites`, `shareEarn`, `profile`.
 *
 * Resolution order:
 *   1. `localStorage['dealalert_locale']` (overrides everything; set by
 *      the LanguageSwitcher component when the user picks a language)
 *   2. `users.preferred_language` from backend (populated via AuthContext
 *      after login; mirrored to localStorage so it survives reloads)
 *   3. `navigator.language` (best-match `de`/`en`/`hr`)
 *   4. fallback `de` (source language for DealAlert)
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import deCommon from './locales/de/common.json';
import deDiscover from './locales/de/discover.json';
import enCommon from './locales/en/common.json';
import enDiscover from './locales/en/discover.json';
import hrCommon from './locales/hr/common.json';
import hrDiscover from './locales/hr/discover.json';
import deLanding from './locales/de/landing.json';
import enLanding from './locales/en/landing.json';
import hrLanding from './locales/hr/landing.json';
import deAuth from './locales/de/auth.json';
import enAuth from './locales/en/auth.json';
import hrAuth from './locales/hr/auth.json';
import deDashboard from './locales/de/dashboard.json';
import enDashboard from './locales/en/dashboard.json';
import hrDashboard from './locales/hr/dashboard.json';

// Sprint M Phase 2 — 4 new surfaces (Top Deals, Favorites, Share & Earn, Profile).
import deTopdeals from './locales/de/topdeals.json';
import enTopdeals from './locales/en/topdeals.json';
import hrTopdeals from './locales/hr/topdeals.json';
import deFavorites from './locales/de/favorites.json';
import enFavorites from './locales/en/favorites.json';
import hrFavorites from './locales/hr/favorites.json';
import deShareEarn from './locales/de/shareEarn.json';
import enShareEarn from './locales/en/shareEarn.json';
import hrShareEarn from './locales/hr/shareEarn.json';
import deProfile from './locales/de/profile.json';
import enProfile from './locales/en/profile.json';
import hrProfile from './locales/hr/profile.json';

// Welle 3 — Internationalisierung: market signals, listing detail,
// heatmap, preferences (filters), API errors.
import deMarket from './locales/de/market.json';
import enMarket from './locales/en/market.json';
import hrMarket from './locales/hr/market.json';
import deListing from './locales/de/listing.json';
import enListing from './locales/en/listing.json';
import hrListing from './locales/hr/listing.json';
import deHeatmap from './locales/de/heatmap.json';
import enHeatmap from './locales/en/heatmap.json';
import hrHeatmap from './locales/hr/heatmap.json';
import dePreferences from './locales/de/preferences.json';
import enPreferences from './locales/en/preferences.json';
import hrPreferences from './locales/hr/preferences.json';
import deErrors from './locales/de/errors.json';
import enErrors from './locales/en/errors.json';
import hrErrors from './locales/hr/errors.json';
import deAi from './locales/de/ai.json';
import enAi from './locales/en/ai.json';
import hrAi from './locales/hr/ai.json';
// Welle 3 Quality Sweep — enums (central label registry), billing,
// support + `mobile` (app-surface strings shared 1:1 with the Expo app).
import deEnums from './locales/de/enums.json';
import enEnums from './locales/en/enums.json';
import hrEnums from './locales/hr/enums.json';
import deBilling from './locales/de/billing.json';
import enBilling from './locales/en/billing.json';
import hrBilling from './locales/hr/billing.json';
import deSupport from './locales/de/support.json';
import enSupport from './locales/en/support.json';
import hrSupport from './locales/hr/support.json';
import deMobile from './locales/de/mobile.json';
// Welle 4 — Admin V2 platform dashboard.
import deAdmin from './locales/de/admin.json';
import enAdmin from './locales/en/admin.json';
import hrAdmin from './locales/hr/admin.json';
import enMobile from './locales/en/mobile.json';
import hrMobile from './locales/hr/mobile.json';
// Pre-EAS Sprint — Deal-Share MVP.
import deShare from './locales/de/share.json';
import enShare from './locales/en/share.json';
import hrShare from './locales/hr/share.json';
// Meta Pixel Sprint 2026-08 — Cookie consent banner + settings modal.
import deConsent from './locales/de/consent.json';
import enConsent from './locales/en/consent.json';
import hrConsent from './locales/hr/consent.json';
// SEO-Sprint 06.10.2026 — Rahmentexte der öffentlichen Einstiegsseiten
// (Titel/H1/Intro je Seite stehen in src/seo/landing-pages.json).
import deSeo from './locales/de/seo.json';
import enSeo from './locales/en/seo.json';
import hrSeo from './locales/hr/seo.json';

export const SUPPORTED_LOCALES = ["de", "en", "hr"];

export const DEFAULT_LOCALE = "de";
export const LOCALE_STORAGE_KEY = 'dealalert_locale';

export const resources = {
  de: {
    common: deCommon, landing: deLanding, auth: deAuth, dashboard: deDashboard,
    topdeals: deTopdeals, favorites: deFavorites, shareEarn: deShareEarn, profile: deProfile,
    market: deMarket, listing: deListing, heatmap: deHeatmap,
    preferences: dePreferences, errors: deErrors, ai: deAi,
    enums: deEnums, billing: deBilling, support: deSupport, mobile: deMobile,
    admin: deAdmin, share: deShare,
    consent: deConsent, discover: deDiscover, seo: deSeo,
  },
  en: {
    common: enCommon, landing: enLanding, auth: enAuth, dashboard: enDashboard,
    topdeals: enTopdeals, favorites: enFavorites, shareEarn: enShareEarn, profile: enProfile,
    market: enMarket, listing: enListing, heatmap: enHeatmap,
    preferences: enPreferences, errors: enErrors, ai: enAi,
    enums: enEnums, billing: enBilling, support: enSupport, mobile: enMobile,
    admin: enAdmin, share: enShare,
    consent: enConsent, discover: enDiscover, seo: enSeo,
  },
  hr: {
    common: hrCommon, landing: hrLanding, auth: hrAuth, dashboard: hrDashboard,
    topdeals: hrTopdeals, favorites: hrFavorites, shareEarn: hrShareEarn, profile: hrProfile,
    market: hrMarket, listing: hrListing, heatmap: hrHeatmap,
    preferences: hrPreferences, errors: hrErrors, ai: hrAi,
    enums: hrEnums, billing: hrBilling, support: hrSupport, mobile: hrMobile,
    admin: hrAdmin, share: hrShare,
    consent: hrConsent, discover: hrDiscover, seo: hrSeo,
  },
};

// P0 Hotfix 2026-06 — never render raw technical keys; humanize instead.
export function humanizeMissingKey(key, defaultValue) {
  if (defaultValue && defaultValue !== key) return defaultValue;
  const last = (key.split(':').pop() || key).split('.').pop() || key;
  const words = last
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    parseMissingKeyHandler: humanizeMissingKey,
    supportedLngs: SUPPORTED_LOCALES,
    nonExplicitSupportedLngs: true,
    ns: ['common', 'landing', 'auth', 'dashboard', 'topdeals', 'favorites', 'shareEarn', 'profile',
         'market', 'listing', 'heatmap', 'preferences', 'errors', 'ai',
         'enums', 'billing', 'support', 'mobile', 'admin', 'share', 'discover',
         'consent', 'seo'],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    react: { useSuspense: false },
  });

export default i18n;
